import React, { useState, useEffect } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Search,
  MessageSquare,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
} from "lucide-react";
import { cn, formatDateTime } from "@/lib/utils";
import { useChatSessionsList } from "@/hooks/useChatSessionsList";
import ChatSidebar from "@/components/chat/ChatSidebar";
import type { ChatSessionWithLatest } from "@/services/chat";
import {
  getSessionIdFromUrl,
  updateUrlQuery,
  clearUrlQuery,
} from "@/utils/navigationUtils";

const MessagesPage: React.FC = () => {
  const [selectedSession, setSelectedSession] =
    useState<ChatSessionWithLatest | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const {
    sessions,
    totalUnreadCount,
    isLoading,
    error,
    refetch,
    markSessionAsRead,
  } = useChatSessionsList();

  // Update selectedSession when sessions data changes (e.g., status updates)
  useEffect(() => {
    if (selectedSession && sessions.length > 0) {
      const updatedSession = sessions.find(
        (sessionData) => sessionData.session.id === selectedSession.session.id
      );
      
      if (updatedSession && updatedSession.session.status !== selectedSession.session.status) {
        console.log("🔄 Updating selected session status:", updatedSession.session.status);
        setSelectedSession(updatedSession);
      }
    }
  }, [sessions, selectedSession]);

  // Handle URL-based session selection on component mount and when sessions change
  useEffect(() => {
    console.log("I AM IN THAT SHITTY USEEFFCT");
    const urlSessionId = getSessionIdFromUrl();

    if (urlSessionId && sessions.length > 0) {
      // Find session by ID from URL
      const sessionToSelect = sessions.find(
        (sessionData) => sessionData.session.id === urlSessionId
      );

      if (
        sessionToSelect &&
        (!selectedSession || selectedSession.session.id !== urlSessionId)
      ) {
        console.log("🔗 Auto-selecting session from URL:", urlSessionId);
        setSelectedSession(sessionToSelect);

        // Mark messages as read when auto-opening session
        if (sessionToSelect.unreadCount > 0) {
          markSessionAsRead(sessionToSelect.session.id);
        }
      } else if (!sessionToSelect && urlSessionId) {
        // Session ID in URL doesn't exist, clear it
        console.warn(
          "⚠️ Session ID from URL not found, clearing URL:",
          urlSessionId
        );
        clearUrlQuery();
      }
    }
  }, [sessions, selectedSession, markSessionAsRead]);

  // Filter sessions based on search query
  const filteredSessions = sessions.filter(
    (sessionData) =>
      sessionData.student.name
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      sessionData.latestMessage?.message
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
  );

  const handleSessionClick = async (sessionData: ChatSessionWithLatest) => {
    setSelectedSession(sessionData);

    // Update URL to reflect selected session
    updateUrlQuery({ sessionId: sessionData.session.id });

    // Mark messages as read when opening session
    if (sessionData.unreadCount > 0) {
      await markSessionAsRead(sessionData.session.id);
    }
  };

  const handleCloseChatSidebar = () => {
    setSelectedSession(null);

    // Clear sessionId from URL when closing chat
    updateUrlQuery({ sessionId: null });
  };

  const getInitials = (name: string) => {
    if (name) {
      return name
        .split(" ")
        .map((n) => n.charAt(0))
        .join("")
        .toUpperCase();
    }
    return "";
  };

  const formatLastActivityTime = (timestamp: string) => {
    const now = new Date();
    const messageTime = new Date(timestamp);
    const diffInMs = now.getTime() - messageTime.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays === 1) return "Yesterday";
    if (diffInDays < 7) return `${diffInDays}d ago`;

    return formatDateTime(timestamp);
  };

  const truncateMessage = (message: string, maxLength: number = 60) => {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + "...";
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case "active":
        return {
          variant: "default" as const,
          className:
            "bg-green-200 text-green-800 border-green-200 animate-pulse hover:bg-green-200",
          icon: CheckCircle,
          label: "Active",
        };
      case "ended":
        return {
          variant: "secondary" as const,
          className: "bg-blue-100 text-blue-800 border-blue-200",
          icon: CheckCircle,
          label: "Completed",
        };
      case "rejected":
        return {
          variant: "destructive" as const,
          className: "bg-red-100 text-red-800 border-red-200 hover:bg-white",
          icon: XCircle,
          label: "Cancelled",
        };
      case "pending":
        return {
          variant: "secondary" as const,
          className: "bg-yellow-100 text-yellow-800 border-yellow-200",
          icon: AlertCircle,
          label: "Pending",
        };
      default:
        return {
          variant: "secondary" as const,
          className: "bg-gray-100 text-gray-800 border-gray-200",
          icon: AlertCircle,
          label: status,
        };
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full">
        <Card className="flex-1 flex flex-col h-full rounded-none border-r">
          <CardHeader className="pb-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-6 h-6 bg-gray-200 rounded animate-pulse"></div>
              <div className="space-y-2">
                <div className="h-6 bg-gray-200 rounded w-24 animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
              </div>
            </div>
            <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
          </CardHeader>
          <CardContent className="flex-1 p-0">
            <div className="space-y-1">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="p-4 border-b border-gray-100">
                  <div className="flex items-start space-x-3">
                    <div className="w-12 h-12 bg-gray-200 rounded-full animate-pulse"></div>
                    <div className="flex-1 space-y-2">
                      <div className="flex justify-between">
                        <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                        <div className="h-3 bg-gray-200 rounded w-16 animate-pulse"></div>
                      </div>
                      <div className="h-3 bg-gray-200 rounded w-40 animate-pulse"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Error loading messages
          </h3>
          <p className="text-gray-600 mb-4">
            There was an error loading your messages.
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full">
      {/* Messages List */}
      <Card className="flex-1 flex flex-col h-full rounded-none border-r">
        {/* Header */}
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <MessageSquare className="h-6 w-6 text-primary" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Messages</h1>
                <p className="text-sm text-gray-600">
                  {sessions.length} conversation
                  {sessions.length !== 1 ? "s" : ""}
                  {totalUnreadCount > 0 && (
                    <span className="ml-2">• {totalUnreadCount} unread</span>
                  )}
                </p>
              </div>
            </div>

            {/* Connection Status */}
            {/* <div className="flex items-center space-x-2">
              <div className={cn(
                "w-2 h-2 rounded-full",
                isConnected ? "bg-green-500" : "bg-red-500"
              )} />
              <span className="text-xs text-gray-500">
                {isConnected ? 'Connected' : 'Disconnected'}
              </span>
            </div> */}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardHeader>

        {/* Conversations List */}
        <CardContent className="flex-1 p-0 overflow-hidden">
          <ScrollArea className="h-full">
            {filteredSessions.length === 0 ? (
              <div className="p-8 text-center">
                <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {searchQuery
                    ? "No conversations found"
                    : "No conversations yet"}
                </h3>
                <p className="text-gray-600">
                  {searchQuery
                    ? "Try adjusting your search terms"
                    : "Start chatting with students to see conversations here"}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredSessions.map((sessionData) => (
                  <div
                    key={sessionData.session.id}
                    onClick={() => handleSessionClick(sessionData)}
                    className={cn(
                      "p-4 hover:bg-gray-50 cursor-pointer transition-colors",
                      selectedSession?.session.id === sessionData.session.id &&
                        "bg-blue-50 border-r-2 border-primary"
                    )}
                  >
                    <div className="flex items-start space-x-3">
                      {/* Avatar */}
                      <Avatar className="w-12 h-12 flex-shrink-0">
                        <AvatarImage
                          src={sessionData.student.profilePicture}
                          alt={sessionData.student.name}
                        />
                        <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white">
                          {getInitials(sessionData.student.name)}
                        </AvatarFallback>
                      </Avatar>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center space-x-2">
                            <h3 className="font-semibold text-gray-900 truncate">
                              {sessionData.student.name}
                            </h3>
                            <Badge
                              variant={
                                getStatusConfig(sessionData.session.status)
                                  .variant
                              }
                              className={
                                getStatusConfig(sessionData.session.status)
                                  .className
                              }
                            >
                              {sessionData.session.status}
                            </Badge>
                          </div>

                          <div className="flex items-center space-x-2 flex-shrink-0">
                            {sessionData.unreadCount > 0 && (
                              <Badge variant="destructive" className="text-xs">
                                {sessionData.unreadCount}
                              </Badge>
                            )}
                            <div className="flex items-center space-x-1 text-xs text-gray-500">
                              <Clock className="h-3 w-3" />
                              <span>
                                {sessionData.latestMessage
                                  ? formatLastActivityTime(
                                      sessionData.latestMessage.sentAt
                                    )
                                  : formatLastActivityTime(
                                      sessionData.session.startedAt
                                    )}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Latest Message Preview */}
                        <div className="flex items-center justify-between">
                          <p
                            className={cn(
                              "text-sm truncate",
                              sessionData.unreadCount > 0
                                ? "text-gray-900 font-medium"
                                : "text-gray-600"
                            )}
                          >
                            {sessionData.latestMessage ? (
                              <>
                                {sessionData.latestMessage.senderId ===
                                  sessionData.session.counselorId && (
                                  <span className="text-blue-600">You: </span>
                                )}
                                {truncateMessage(
                                  sessionData.latestMessage.message
                                )}
                              </>
                            ) : (
                              <span className="italic">No messages yet</span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Chat Sidebar */}
      {selectedSession && (
        <div className="fixed top-0 right-0 h-screen z-50">
          <ChatSidebar
            student={{
              id: selectedSession.student.id,
              name: selectedSession.student.name,
              email: selectedSession.student.email || "",
              phoneNumber: selectedSession.student.phoneNumber || "",
              userType: "student",
              profilePicture: selectedSession.student.profilePicture || "",
            }}
            sessionId={selectedSession.session.id}
            sessionStatus={selectedSession.session.status}
            onClose={handleCloseChatSidebar}
            refetchChatSessions={() => {
              refetch();
              setSelectedSession(null);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default MessagesPage;
